import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Mic } from 'lucide-react';
import { Textarea } from '@/components/ui/textarea';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const CONFIRM = {
  voice: { title: 'Switch to Voice?', body: 'Your typed response will be cleared.', ok: 'Clear text & record' },
  text: { title: 'Switch to Text?', body: 'Your voice recording will be removed.', ok: 'Remove & type' },
};

function SwitchConfirm({ kind, onCancel, onConfirm }) {
  const c = CONFIRM[kind] || CONFIRM.voice;
  return (
    <AlertDialog open={!!kind} onOpenChange={(o) => { if (!o) onCancel(); }}>
      <AlertDialogContent className="max-w-[calc(100vw-2rem)] sm:max-w-md rounded-xl" data-testid="textvoice-confirm-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle data-testid="textvoice-confirm-title">{c.title}</AlertDialogTitle>
          <AlertDialogDescription>{c.body}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="textvoice-confirm-cancel">Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={onConfirm} className="bg-[#214A7B] hover:bg-[#1A3E6F]" data-testid="textvoice-confirm-ok">{c.ok}</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

// Text + Optional Voice (standard_2026, reusable): text is the default method;
// voice is an optional alternative. Only one method is ever active/submitted.
export function TextVoice2026({ question, answer, onChange, onVoiceBusyChange, renderRecorder }) {
  // Rebuild from the saved answer (e.g. after Back) so content is never silently lost.
  const restoredRef = useRef(answer?.blob instanceof Blob ? answer : null);
  const restored = restoredRef.current;
  const [text, setText] = useState(() => (!restored && typeof answer?.text === 'string' ? answer.text : ''));
  const [voiceOn, setVoiceOn] = useState(!!restored);
  const [recorded, setRecorded] = useState(!!restored);
  const [busy, setBusy] = useState(false);
  const [recKey, setRecKey] = useState(0);
  const [confirm, setConfirm] = useState(null);
  const textRef = useRef(null);
  const hasText = text.trim().length > 0;
  const textLocked = voiceOn && (busy || recorded);
  const qid = question.id;

  useEffect(() => {
    if (answer && !restored && !(typeof answer.text === 'string' && answer.text.trim())) onChange(undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleBusy = useCallback((b) => { setBusy(b); onVoiceBusyChange?.(b); }, [onVoiceBusyChange]);
  const handleText = (val) => {
    setText(val);
    if (voiceOn && !recorded && !busy) setVoiceOn(false);
    onChange(val.trim() ? { text: val } : undefined);
  };
  const startVoice = () => { setRecKey((k) => k + 1); setRecorded(false); setVoiceOn(true); };
  const removeVoice = () => { setVoiceOn(false); setRecorded(false); setRecKey((k) => k + 1); onChange(undefined); };
  const onRecordTap = () => (hasText ? setConfirm('voice') : startVoice());
  const onConfirm = () => {
    if (confirm === 'voice') { setText(''); onChange(undefined); startVoice(); }
    else { removeVoice(); setTimeout(() => textRef.current?.focus(), 50); }
    setConfirm(null);
  };

  return (
    <div className="std2026-textvoice" data-testid={`textvoice-${qid}`} data-active-method={voiceOn ? 'voice' : 'text'}>
      <div className="relative">
        <Textarea
          ref={textRef}
          rows={4}
          value={text}
          disabled={textLocked}
          onChange={(e) => handleText(e.target.value)}
          placeholder={question.placeholder_text || 'Type your answer here…'}
          aria-label="Your answer"
          className="min-h-[112px] text-base disabled:opacity-50 disabled:bg-[#F8FAFC]"
          style={{ borderColor: '#E2E8F0' }}
          data-testid={`voice-text-primary-${qid}`}
        />
        {textLocked && recorded && (
          <button type="button" onClick={() => setConfirm('text')} className="absolute inset-0 w-full rounded-md cursor-pointer" aria-label="Type your response instead" data-testid={`textvoice-text-locked-${qid}`} />
        )}
      </div>
      {textLocked && (
        <p className="mt-1.5 text-xs text-[#64748B]" data-testid={`textvoice-text-helper-${qid}`}>
          {recorded ? 'Remove the recording to type your response instead.' : 'Recording your voice response…'}
        </p>
      )}

      <div className="mt-4" data-testid={`voice-secondary-${qid}`}>
        {!voiceOn ? (
          <>
            <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
              <span className="text-sm text-[#64748B]">Prefer to speak instead?</span>
              <button
                type="button"
                onClick={onRecordTap}
                aria-describedby={hasText ? `textvoice-voice-helper-${qid}` : undefined}
                className={`inline-flex items-center gap-2 min-h-[44px] px-4 rounded-full border text-sm font-semibold transition-colors ${hasText ? 'border-dashed border-[#CBD5E1] bg-[#F8FAFC] text-[#94A3B8]' : 'border-[#CBD5E1] bg-white text-[#334155] hover:bg-[#F1F5F9]'}`}
                data-testid={`textvoice-record-btn-${qid}`}
                data-locked={hasText ? 'true' : 'false'}
              >
                <Mic className="w-4 h-4" /> Record Voice
              </button>
            </div>
            {hasText && (
              <p id={`textvoice-voice-helper-${qid}`} className="mt-1.5 text-xs text-[#94A3B8] sm:text-right" data-testid={`textvoice-voice-helper-${qid}`}>Clear your text to record a voice response.</p>
            )}
          </>
        ) : (
          <div className="rounded-xl border border-[#E2E8F0] bg-[#F8FAFC] p-3 overflow-hidden" data-testid={`textvoice-voice-panel-${qid}`}>
            <p className="text-xs font-semibold uppercase tracking-wide text-[#214A7B] mb-2">{recorded || busy ? 'Answering by voice' : 'Voice response'}</p>
            {renderRecorder({
              key: recKey,
              initial: recKey === 0 ? restored : null,
              autoStart: !(recKey === 0 && restored),
              onBusyChange: handleBusy,
              onResult: (r) => { setRecorded(true); onChange(r); },
              onRemove: removeVoice,
            })}
          </div>
        )}
      </div>
      <SwitchConfirm kind={confirm} onCancel={() => setConfirm(null)} onConfirm={onConfirm} />
    </div>
  );
}
